import type { jsPDF } from "jspdf";

type Style = { bold: boolean; italic: boolean; sup: boolean };

export type PdfExportLayout = {
    margin: number;
    bodyFontSize: number;
    bodyLineMm: number;
    pageWidth: number;
    pageHeight: number;
};

const defaultStyle = (): Style => ({ bold: false, italic: false, sup: false });

function mergeStyleChunks(chunks: Array<{ text: string } & Style>): Array<{ text: string } & Style> {
    const out: Array<{ text: string } & Style> = [];
    for (const ch of chunks) {
        if (!ch.text) {
            continue;
        }
        const prev = out[out.length - 1];
        if (prev && prev.bold === ch.bold && prev.italic === ch.italic && prev.sup === ch.sup) {
            prev.text += ch.text;
        } else {
            out.push({ ...ch });
        }
    }
    return out;
}

function flattenInline(node: Node, inherited: Style): Array<{ text: string } & Style> {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? "";
        return text ? [{ text, ...inherited }] : [];
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return [];
    }
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === "br") {
        return [{ text: "\n", ...inherited }];
    }
    let st = { ...inherited };
    if (tag === "strong" || tag === "b") {
        st = { ...st, bold: true };
    }
    if (tag === "em" || tag === "i") {
        st = { ...st, italic: true };
    }
    if (tag === "sup") {
        st = { ...st, sup: true };
    }
    if (tag === "a") {
        if (el.classList.contains("supContainer")) {
            const num = (el.textContent ?? "").trim();
            return [{ text: num || "?", ...st, sup: true }];
        }
        const inner: Array<{ text: string } & Style> = [];
        for (const c of el.childNodes) {
            inner.push(...flattenInline(c, st));
        }
        return mergeStyleChunks(inner);
    }
    if (tag === "code") {
        return [{ text: el.textContent ?? "", ...st }];
    }
    const out: Array<{ text: string } & Style> = [];
    for (const c of el.childNodes) {
        out.push(...flattenInline(c, st));
    }
    return mergeStyleChunks(out);
}

type WordPiece = { text: string } & Style;

/** Words and explicit line breaks from inline chunks (preserves styles per word). */
function chunksToTokens(chunks: Array<{ text: string } & Style>): Array<WordPiece | "nl"> {
    const merged = mergeStyleChunks(chunks);
    const out: Array<WordPiece | "nl"> = [];
    for (const ch of merged) {
        const segments = ch.text.split("\n");
        for (let i = 0; i < segments.length; i++) {
            if (i > 0) {
                out.push("nl");
            }
            const words = segments[i].split(/\s+/).filter(w => w.length > 0);
            for (const w of words) {
                out.push({ text: w, bold: ch.bold, italic: ch.italic, sup: ch.sup });
            }
        }
    }
    return out;
}

function setFontForStyle(pdf: jsPDF, piece: Style, baseSize: number) {
    const size = piece.sup ? baseSize * 0.75 : baseSize;
    pdf.setFontSize(size);
    if (piece.bold && piece.italic) {
        pdf.setFont("helvetica", "bolditalic");
    } else if (piece.bold) {
        pdf.setFont("helvetica", "bold");
    } else if (piece.italic) {
        pdf.setFont("helvetica", "italic");
    } else {
        pdf.setFont("helvetica", "normal");
    }
}

function measurePiece(pdf: jsPDF, piece: WordPiece, baseSize: number): number {
    setFontForStyle(pdf, piece, baseSize);
    return pdf.getTextWidth(piece.text);
}

function spaceWidth(pdf: jsPDF, fontSize: number): number {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(fontSize);
    return pdf.getTextWidth(" ");
}

function drawRichLine(
    pdf: jsPDF,
    pieces: WordPiece[],
    xStart: number,
    y: number,
    baseSize: number,
    supLiftMm: number
): void {
    let x = xStart;
    pieces.forEach((p, i) => {
        if (i > 0) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(baseSize);
            pdf.text(" ", x, y);
            x += spaceWidth(pdf, baseSize);
        }
        setFontForStyle(pdf, p, baseSize);
        const drawY = p.sup ? y - supLiftMm : y;
        pdf.text(p.text, x, drawY);
        x += pdf.getTextWidth(p.text);
    });
}

function layoutRichParagraph(
    pdf: jsPDF,
    chunks: Array<{ text: string } & Style>,
    layout: PdfExportLayout,
    yRef: { y: number },
    maxLineWidth: number,
    xMargin: number,
    fontSize: number,
    lineMm: number,
    firstLinePrefix: string | null,
    hangIndentMm: number
): void {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(fontSize);
    const prefixW = firstLinePrefix ? pdf.getTextWidth(firstLinePrefix) : 0;

    const tokens = chunksToTokens(chunks);
    const lines: WordPiece[][] = [];
    let current: WordPiece[] = [];
    let lineW = 0;

    const flush = () => {
        if (current.length > 0) {
            lines.push(current);
            current = [];
            lineW = 0;
        }
    };

    const limitForLine = () => {
        if (lines.length === 0) {
            return firstLinePrefix ? maxLineWidth - prefixW : maxLineWidth;
        }
        return maxLineWidth - hangIndentMm;
    };

    for (const t of tokens) {
        if (t === "nl") {
            flush();
            continue;
        }
        const pieceW = measurePiece(pdf, t, fontSize);
        const sp = current.length > 0 ? spaceWidth(pdf, fontSize) : 0;
        const lim = limitForLine();
        if (current.length > 0 && lineW + sp + pieceW > lim + 0.01) {
            flush();
        }
        lineW += (current.length > 0 ? sp : 0) + pieceW;
        current.push(t);
    }
    flush();

    const ensureRoom = (needed: number) => {
        if (yRef.y + needed > layout.pageHeight - layout.margin) {
            pdf.addPage();
            yRef.y = layout.margin + 4;
        }
    };

    const supLift = fontSize * 0.12;

    lines.forEach((line, idx) => {
        ensureRoom(lineMm);
        if (idx === 0 && firstLinePrefix) {
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(fontSize);
            pdf.text(firstLinePrefix, xMargin, yRef.y);
        }
        const wordStartX = xMargin + (idx === 0 && firstLinePrefix ? prefixW : hangIndentMm);
        drawRichLine(pdf, line, wordStartX, yRef.y, fontSize, supLift);
        yRef.y += lineMm;
    });
}

function richChunksFromElement(el: HTMLElement): Array<{ text: string } & Style> {
    const raw: Array<{ text: string } & Style> = [];
    for (const c of el.childNodes) {
        raw.push(...flattenInline(c, defaultStyle()));
    }
    return mergeStyleChunks(raw);
}

function renderList(
    pdf: jsPDF,
    list: HTMLElement,
    layout: PdfExportLayout,
    yRef: { y: number },
    indentMm: number
): void {
    const isOrdered = list.tagName.toLowerCase() === "ol";
    const items = Array.from(list.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName.toLowerCase() === "li");

    items.forEach((li, index) => {
        const inlineContainer = document.createElement("div");
        Array.from(li.childNodes).forEach(child => {
            if (child.nodeType === Node.ELEMENT_NODE) {
                const childEl = child as HTMLElement;
                const tag = childEl.tagName.toLowerCase();
                if (tag === "ul" || tag === "ol") {
                    return;
                }
            }
            inlineContainer.appendChild(child.cloneNode(true));
        });

        const chunks = richChunksFromElement(inlineContainer);
        if (chunks.some(chunk => chunk.text.trim())) {
            const prefix = isOrdered ? `${index + 1}. ` : "• ";
            layoutRichParagraph(
                pdf,
                chunks,
                layout,
                yRef,
                layout.pageWidth - 2 * layout.margin - indentMm,
                layout.margin + indentMm,
                layout.bodyFontSize,
                layout.bodyLineMm,
                prefix,
                4
            );
            yRef.y += 1;
        }

        Array.from(li.children)
            .filter((child): child is HTMLElement => child instanceof HTMLElement)
            .forEach(child => {
                const tag = child.tagName.toLowerCase();
                if (tag === "ul" || tag === "ol") {
                    renderList(pdf, child, layout, yRef, indentMm + 6);
                }
            });
    });
}

function renderTable(pdf: jsPDF, table: HTMLTableElement, layout: PdfExportLayout, yRef: { y: number }): void {
    const rows = Array.from(table.rows);
    if (rows.length === 0) {
        return;
    }

    const tableWidth = layout.pageWidth - 2 * layout.margin;
    const cellPaddingX = 1.5;
    const cellPaddingY = 1.5;
    const fontSize = Math.max(8.5, layout.bodyFontSize - 0.5);
    const lineHeight = 4.2;
    const minRowHeight = 7;
    const colCount = Math.max(...rows.map(row => row.cells.length));
    const isHeaderRow = (row: HTMLTableRowElement, index: number) =>
        index === 0 && (row.parentElement?.tagName.toLowerCase() === "thead" || Array.from(row.cells).every(cell => cell.tagName.toLowerCase() === "th"));

    const normalizedRows = rows.map(row =>
        Array.from({ length: colCount }, (_, index) => {
            const cell = row.cells[index];
            return cell ? cell.innerText.replace(/\s+/g, " ").trim() : "";
        })
    );

    const colWeights = Array.from({ length: colCount }, (_, colIndex) => {
        const maxLen = Math.max(...normalizedRows.map(row => row[colIndex].length), 1);
        return Math.max(1, Math.min(6, Math.sqrt(maxLen)));
    });
    const totalWeight = colWeights.reduce((sum, weight) => sum + weight, 0);
    const colWidths = colWeights.map(weight => (weight / totalWeight) * tableWidth);

    const ensureRoom = (neededHeight: number, repeatHeader: boolean) => {
        if (yRef.y + neededHeight <= layout.pageHeight - layout.margin) {
            return;
        }
        pdf.addPage();
        yRef.y = layout.margin + 4;
        if (repeatHeader && rows.length > 0 && isHeaderRow(rows[0], 0)) {
            drawRow(rows[0], normalizedRows[0], true);
        }
    };

    const drawRow = (row: HTMLTableRowElement, cells: string[], forceHeader = false) => {
        const header = forceHeader || isHeaderRow(row, rows.indexOf(row));
        const wrappedCells = cells.map((text, colIndex) => {
            pdf.setFont("helvetica", header ? "bold" : "normal");
            pdf.setFontSize(fontSize);
            return pdf.splitTextToSize(text, Math.max(8, colWidths[colIndex] - cellPaddingX * 2));
        });
        const rowHeight = Math.max(
            minRowHeight,
            ...wrappedCells.map(lines => Math.max(minRowHeight, lines.length * lineHeight + cellPaddingY * 2))
        );

        ensureRoom(rowHeight, !forceHeader);

        let x = layout.margin;
        wrappedCells.forEach((lines, colIndex) => {
            const width = colWidths[colIndex];
            if (header) {
                pdf.setFillColor(248, 249, 251);
                pdf.rect(x, yRef.y, width, rowHeight, "FD");
            } else {
                pdf.rect(x, yRef.y, width, rowHeight);
            }

            pdf.setFont("helvetica", header ? "bold" : "normal");
            pdf.setFontSize(fontSize);
            lines.forEach((line: string, lineIndex: number) => {
                const textY = yRef.y + cellPaddingY + 3 + lineIndex * lineHeight;
                pdf.text(line, x + cellPaddingX, textY);
            });
            x += width;
        });

        yRef.y += rowHeight;
    };

    rows.forEach((row, index) => {
        drawRow(row, normalizedRows[index]);
    });

    yRef.y += 3;
}

function walkAndRender(
    pdf: jsPDF,
    container: HTMLElement,
    layout: PdfExportLayout,
    yRef: { y: number },
    headingScale: (level: number) => number
): void {
    const maxLineWidth = layout.pageWidth - 2 * layout.margin;
    const margin = layout.margin;

    const ensureRoom = (needed: number) => {
        if (yRef.y + needed > layout.pageHeight - layout.margin) {
            pdf.addPage();
            yRef.y = layout.margin + 4;
        }
    };

    const visitNodes = (nodes: NodeListOf<ChildNode> | ChildNode[]) => {
        for (const node of Array.from(nodes)) {
            if (node.nodeType === Node.TEXT_NODE) {
                const t = (node.textContent ?? "").replace(/\s+/g, " ").trim();
                if (t) {
                    layoutRichParagraph(pdf, [{ text: t, ...defaultStyle() }], layout, yRef, maxLineWidth, margin, layout.bodyFontSize, layout.bodyLineMm, null, 0);
                    yRef.y += 2;
                }
                continue;
            }
            if (node.nodeType !== Node.ELEMENT_NODE) {
                continue;
            }
            const el = node as HTMLElement;
            const tag = el.tagName.toLowerCase();

            if (/^h[1-6]$/.test(tag)) {
                const level = Number.parseInt(tag[1], 10);
                const fs = headingScale(level);
                const raw = richChunksFromElement(el);
                const headingChunks = (raw.some(c => c.text.trim()) ? raw : [{ text: el.innerText.trim(), ...defaultStyle() }]).map(c => ({
                    ...c,
                    bold: true
                }));
                const lineMm = Math.max(4.8, fs * 0.42);
                ensureRoom(4);
                yRef.y += 3;
                layoutRichParagraph(pdf, headingChunks, layout, yRef, maxLineWidth, margin, fs, lineMm, null, 0);
                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(layout.bodyFontSize);
                yRef.y += 2;
                continue;
            }

            if (tag === "p") {
                const chunks = richChunksFromElement(el);
                if (chunks.some(c => c.text.trim())) {
                    layoutRichParagraph(pdf, chunks, layout, yRef, maxLineWidth, margin, layout.bodyFontSize, layout.bodyLineMm, null, 0);
                    yRef.y += 2;
                }
                continue;
            }

            if (tag === "hr") {
                ensureRoom(6);
                yRef.y += 2;
                pdf.setDrawColor(180);
                pdf.setLineWidth(0.2);
                pdf.line(margin, yRef.y, layout.pageWidth - margin, yRef.y);
                pdf.setDrawColor(0);
                yRef.y += 5;
                continue;
            }

            if (tag === "ul" || tag === "ol") {
                renderList(pdf, el, layout, yRef, 4);
                yRef.y += 2;
                continue;
            }

            if (tag === "blockquote") {
                const chunks = richChunksFromElement(el);
                if (chunks.some(c => c.text.trim())) {
                    const qMargin = margin + 5;
                    const qWidth = maxLineWidth - 5;
                    layoutRichParagraph(pdf, chunks, layout, yRef, qWidth, qMargin, layout.bodyFontSize, layout.bodyLineMm, null, 0);
                    yRef.y += 2;
                }
                continue;
            }

            if (tag === "pre") {
                const text = el.innerText.trimEnd();
                if (text) {
                    pdf.setFont("courier", "normal");
                    pdf.setFontSize(layout.bodyFontSize - 0.5);
                    const lines = pdf.splitTextToSize(text, maxLineWidth);
                    for (const line of lines) {
                        ensureRoom(layout.bodyLineMm);
                        pdf.text(line, margin, yRef.y);
                        yRef.y += layout.bodyLineMm;
                    }
                    pdf.setFont("helvetica", "normal");
                    pdf.setFontSize(layout.bodyFontSize);
                    yRef.y += 2;
                }
                continue;
            }

            if (tag === "table") {
                if (el instanceof HTMLTableElement) {
                    renderTable(pdf, el, layout, yRef);
                } else {
                    const text = el.innerText.trim().replace(/\s*\n\s*/g, " | ");
                    if (text) {
                        layoutRichParagraph(pdf, [{ text, ...defaultStyle() }], layout, yRef, maxLineWidth, margin, layout.bodyFontSize, layout.bodyLineMm, null, 0);
                        yRef.y += 2;
                    }
                }
                continue;
            }

            if (tag === "div") {
                visitNodes(el.childNodes);
                continue;
            }

            visitNodes(el.childNodes);
        }
    };

    visitNodes(container.childNodes);
}

/**
 * Writes a text-based PDF from the live rendered answer DOM so the export follows the actual UI structure.
 */
export function writeRenderedAnswerToPdf(
    pdf: jsPDF,
    renderedAnswerElement: HTMLElement,
    layout: PdfExportLayout,
    options: { citations: string[]; citationSectionTitle: string }
): void {
    const container = renderedAnswerElement.cloneNode(true) as HTMLElement;

    if (options.citations.length > 0) {
        const citeBlock = document.createElement("div");
        const h = document.createElement("h3");
        h.textContent = options.citationSectionTitle;
        citeBlock.appendChild(h);
        const ol = document.createElement("ol");
        for (const c of options.citations) {
            const li = document.createElement("li");
            li.textContent = c;
            ol.appendChild(li);
        }
        citeBlock.appendChild(ol);
        container.appendChild(citeBlock);
    }

    const yRef = { y: layout.margin + 4 };

    const headingScale = (level: number) => Math.max(12, 19 - level);

    walkAndRender(pdf, container, layout, yRef, headingScale);
}
